/**
 * 回数上限まわりの判定（免除・案内）と、その表示文字列。
 *
 * 判定の真実源はバックエンドで、ここは画面表示のための写し。式は
 * backend/app/services/plan_limit_checker.py の check / _session_limit_guidance と、
 * DB の record_usage（supabase/migrations/20260928000000_session_packs.sql）に合わせること。
 * 3か所のうち片側だけ変えない。
 *
 * 表示フラグ（BILLING_ENABLED）と導線（リンク先・リンク文言）はページ側で扱う。
 */
import type { UserPlan } from '../types'

export type SessionGuidance = 'buy_sessions' | 'upgrade'

/** 月枠の時間を使い切り、追加時間で書き起こす間は回数上限を適用しない（paying_with_extra_hours） */
export function isSessionLimitExempt(plan: UserPlan): boolean {
  if (plan.plan_type === 'unlimited') return false
  return plan.hours_limit !== -1 && plan.hours_used >= plan.hours_limit && (plan.extra_hours ?? 0) > 0
}

/**
 * 回数が足りず、月枠の時間は残っているときの案内の種類。案内が不要なら null。
 * バックエンドの 403 は月枠の時間も使い切った場合（時間の購入）やフリーの時間切れも案内するが、
 * 画面の案内は「月枠の時間が残っている」状態に限る（それ以外は 403 文言だけで案内する）。
 */
export function sessionGuidance(plan: UserPlan): SessionGuidance | null {
  if (plan.plan_type === 'unlimited') return null
  if (plan.sessions_limit === -1 || isSessionLimitExempt(plan)) return null
  if (plan.sessions_used < plan.sessions_limit || (plan.extra_sessions ?? 0) > 0) return null
  if (plan.hours_limit !== -1 && plan.hours_used >= plan.hours_limit) return null
  if (plan.plan_type === 'free') return 'upgrade'
  // 回数の購入はライト・スタンダードのみ（BillingPage の canBuySessions と同じ条件）
  if (plan.plan_type === 'lite' || plan.plan_type === 'standard') return 'buy_sessions'
  return null
}

export const SESSION_GUIDANCE_MESSAGE: Record<SessionGuidance, string> = {
  buy_sessions: '今月の回数を使い切りました。回数を追加購入すると、続けて書き起こせます。',
  upgrade: '今月の回数を使い切りました。プランをアップグレードすると、続けて書き起こせます。',
}

/** 回数表示に添える「（追加回数 残りN回）」。追加回数が無い・回数無制限なら空文字 */
export function extraSessionsNote(plan: UserPlan): string {
  const extra = plan.extra_sessions ?? 0
  if (plan.plan_type === 'unlimited' || plan.sessions_limit === -1 || extra < 1) return ''
  return `（追加回数 残り${extra}回）`
}
